import type {
  IdentityUser,
  IdentityUserSearchQuery,
} from "../../types/identityUser";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

/** Нормализация телефона для GET /users?phone= */
export function normalizePhoneQuery(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  return hasPlus ? `+${digits}` : digits;
}

/**
 * Поиск CHM.ID: GET /api/v1/users?id= | ?email= | ?phone=
 * Телефон — минимум 5 цифр.
 */
export function detectIdentityUserQuery(
  raw: string,
): IdentityUserSearchQuery | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.includes("@")) return { email: trimmed };
  if (UUID_RE.test(trimmed)) return { id: trimmed };
  const phone = normalizePhoneQuery(trimmed);
  if (phone.replace(/\D/g, "").length >= 5) return { phone };
  return null;
}

export function identityUserDisplayName(user: {
  firstName?: string | null;
  lastName?: string | null;
  middleName?: string | null;
  email?: string | null;
  phone?: string | null;
  id: string;
}): string {
  const name = [user.lastName, user.firstName, user.middleName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return name || user.email?.trim() || user.phone?.trim() || user.id;
}

export function normalizeIdentityUser(raw: unknown): IdentityUser | null {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as Record<string, unknown>;
  const id = readString(item.id) ?? readString(item.chmId) ?? readString(item.chm_id);
  if (!id) return null;

  const firstName = readString(item.firstName ?? item.first_name);
  const lastName = readString(item.lastName ?? item.last_name);
  const middleName = readString(item.middleName ?? item.middle_name);
  const email = readString(item.email);
  const phone = readString(item.phone);

  return {
    id,
    name: identityUserDisplayName({
      id,
      firstName,
      lastName,
      middleName,
      email,
      phone,
    }),
    email,
    phone,
  };
}

export function normalizeIdentityUsers(raw: unknown): IdentityUser[] {
  const rows = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { data?: unknown }).data)
      ? ((raw as { data: unknown[] }).data)
      : [];

  const users: IdentityUser[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const user = normalizeIdentityUser(row);
    if (!user) continue;
    const key = user.id.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    users.push(user);
  }
  return users;
}
